import React, { useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { CustomModal } from "Components/Common/CustomModal";
import { FileUploadCircle } from "Components/Icons/FileUploadCircle";
import { DragAndDropIcon } from "Components/Icons/DragAndDropIcon";
import { CustomInfoCircle } from "Components/Icons/CustomInfoCircle";
import { FileDownload } from "Components/Icons/FileDownload";
import { CustomButton } from "Components/Common/CustomButton";
import { FileIcon } from "Components/Icons/FileIcon";
import { TimesIcon } from "Components/Icons/TimesIcon";
import { useUploadFile } from "@/hooks/curriculum-map";
import { UploadToast } from "./components/UploadToast";

interface ModalUploadProps {
    show: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

enum UploadStatus {
    IDLE = "idle",
    UPLOADING = "uploading",
    COMPLETED = "completed",
    ERROR = "error",
}

interface FileState {
    file: File;
    status: UploadStatus;
    rowCount?: number;
    errorMessage?: string;
}

const templateUrl = "/external/CurriculumMapTemplate.xlsx";

const ModalUpload = ({ show, onClose, onSuccess }: ModalUploadProps) => {
    const [fileState, setFileState] = useState<FileState | null>(null);
    const [showToast, setShowToast] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { uploadFile } = useUploadFile();

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (files.length > 0 && files[0].name.endsWith(".xlsx")) {
            setFileState({ file: files[0], status: UploadStatus.IDLE });
        }
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            try {
                setFileState({ file: files[0], status: UploadStatus.UPLOADING });
                const rowCount = 2; // TODO: Implement an endpoint to get the row count
                setFileState({ file: files[0], status: UploadStatus.COMPLETED, rowCount });
            } catch (error) {
                setFileState({
                    file: files[0],
                    status: UploadStatus.ERROR,
                    errorMessage: "Error: Wrong format"
                });
            }
        }
    };

    const handleDownloadTemplate = () => {
        window.open(templateUrl, "_blank");
    };

    const handleChooseFile = () => {
        fileInputRef.current?.click();
    };

    const handleImport = async () => {
        if (fileState) {
            try {
                await uploadFile({ file: fileState.file });
                setFileState({ file: fileState.file, status: UploadStatus.COMPLETED });
                onClose();
                setShowToast(true);
                onSuccess?.();
            } catch (error: any) {
                setFileState({ file: fileState.file, status: UploadStatus.ERROR, errorMessage: error?.message });
            }
        }
    };

    const handleRemoveFile = () => {
        setFileState(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const renderFileStatus = () => {
        if (!fileState) return null;

        const getStatusColor = () => {
            switch (fileState.status) {
                case UploadStatus.UPLOADING: return "purple";
                case UploadStatus.COMPLETED: return "green";
                case UploadStatus.ERROR: return "red";
                default: return "inherit";
            }
        };

        const getStatusText = () => {
            switch (fileState.status) {
                case UploadStatus.UPLOADING: return "Uploading...";
                case UploadStatus.COMPLETED: return "Completed";
                case UploadStatus.ERROR: return fileState.errorMessage;
                default: return "";
            }
        };

        return (
            <div className="file-status">
                <div className="file-status-container">
                    <FileIcon />
                    <div className="file-info">
                        <span className="file-name">{fileState.file.name}</span>
                        <span className="file-size">{(fileState.file.size / (1024 * 1024)).toFixed(2)} MB</span>
                    </div>
                    <div className="status-text" style={{ color: getStatusColor() }}>
                        {getStatusText()}
                    </div>
                    <button
                        className="remove-file"
                        onClick={handleRemoveFile}
                        aria-label="Remove file"
                    >
                        <TimesIcon />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <>
            <CustomModal
                icon={<FileUploadCircle />}
                title="Import Files"
                show={show}
                onClose={onClose}
                className="curriculum-map-modal"
            >
                <div className="upload-container">
                    {!fileState ? (
                        <div
                            className="drop-zone"
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                        >
                            <div className="upload-text">
                                <DragAndDropIcon /> Drag and drop your files or{" "}
                                <button className="choose-file-link" onClick={handleChooseFile}>
                                    Choose file
                                </button>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx"
                                onChange={handleFileSelect}
                                style={{ display: "none" }}
                                data-testid="file-input"
                            />
                            <div className="supported-files">
                                Supported files: .XLSX
                            </div>
                        </div>
                    ) : (
                        renderFileStatus()
                    )}

                    <div className="template-section">
                        <div className="template-info">
                            <CustomInfoCircle />
                            <div className="template-text">
                                <h4>Download Template</h4>
                                <p>Need a reference? Download the latest template file to get started.</p>
                            </div>
                            <Button
                                variant="link"
                                className="download-button"
                                onClick={handleDownloadTemplate}
                                data-testid="download-button"
                            >
                                <FileDownload />
                            </Button>
                        </div>
                    </div>

                    <div className="modal-actions">
                        <CustomButton
                            variant="outline"
                            onClick={onClose}
                            text="Cancel"
                        />

                        <CustomButton
                            variant="secondary"
                            onClick={handleImport}
                            text={fileState?.status === UploadStatus.COMPLETED ? `Import ${fileState.rowCount} rows` : "Import"}
                            disabled={!fileState || fileState.status === UploadStatus.UPLOADING}
                            testId="import-button"
                        />
                    </div>
                </div>
            </CustomModal>
            <UploadToast
                show={showToast}
                onClose={() => setShowToast(false)}
                title="File uploaded!"
                message="You're going to receive a confirmation email."
            />
        </>
    );
};

export default ModalUpload;
